import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  DollarSign,
  FileText,
  Play,
  RefreshCw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  UserCheck,
  Users,
  X,
} from "lucide-react"
import {
  smartoltService,
  type SmartOLTBillingSuspensionRow,
  type SmartOLTBillingSuspensionStats,
} from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"
import { formatCompactPkr } from "../../../utils/formatCompactPkr.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"

type StatusTab =
  | "all"
  | "suspended_unpaid"
  | "temporary_reactivated"
  | "external_override_detected"
  | "manually_exempted"
  | "resolved_paid"

const PAGE_SIZES = [25, 50, 100, 200]

const tabs: Array<{ key: StatusTab; label: string }> = [
  { key: "all", label: "All records" },
  { key: "suspended_unpaid", label: "Suspended" },
  { key: "temporary_reactivated", label: "Temporary" },
  { key: "external_override_detected", label: "External override" },
  { key: "resolved_paid", label: "Resolved" },
]

function formatDate(value?: string | null, withTime = false) {
  if (!value) return "-"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return withTime ? date.toLocaleString() : date.toLocaleDateString()
}

function statusMeta(status: string) {
  if (status === "suspended_unpaid") {
    return {
      label: "Suspended",
      icon: Ban,
      className: "border-rose-200 bg-rose-50 text-rose-700",
      dot: "bg-rose-500",
    }
  }
  if (status === "manually_exempted") {
    return {
      label: "Owner exempted",
      icon: ShieldCheck,
      className: "border-amber-200 bg-amber-50 text-amber-800",
      dot: "bg-amber-500",
    }
  }
  if (status === "temporary_reactivated") {
    return {
      label: "Temporary Reactivated",
      icon: Clock,
      className: "border-amber-200 bg-amber-50 text-amber-800",
      dot: "bg-amber-500",
    }
  }
  if (status === "external_override_detected") {
    return {
      label: "External Override",
      icon: AlertTriangle,
      className: "border-orange-200 bg-orange-50 text-orange-800",
      dot: "bg-orange-500",
    }
  }
  if (status === "resolved_paid") {
    return {
      label: "Resolved",
      icon: CheckCircle2,
      className: "border-emerald-200 bg-emerald-50 text-emerald-700",
      dot: "bg-emerald-500",
    }
  }
  return {
    label: status || "Unknown",
    icon: Clock,
    className: "border-slate-200 bg-slate-100 text-slate-700",
    dot: "bg-slate-400",
  }
}

function StatTile({
  label,
  value,
  hint,
  tone,
  icon: Icon,
}: {
  label: string
  value: string | number
  hint: string
  tone: "rose" | "amber" | "emerald" | "slate"
  icon: React.ElementType
}) {
  const toneClass =
    tone === "rose"
      ? "text-rose-700 bg-rose-50 border-rose-100"
      : tone === "amber"
        ? "text-amber-800 bg-amber-50 border-amber-100"
        : tone === "emerald"
          ? "text-emerald-700 bg-emerald-50 border-emerald-100"
          : "text-slate-700 bg-slate-50 border-slate-100"

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
          <p className="mt-2 text-2xl font-bold text-slate-950">{value}</p>
        </div>
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg border ${toneClass}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <p className="mt-2 text-xs text-slate-500">{hint}</p>
    </div>
  )
}

export function SmartOLTBillingSuspensionsPage() {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [workerRunning, setWorkerRunning] = useState(false)
  const [suspensions, setSuspensions] = useState<SmartOLTBillingSuspensionRow[]>([])
  const [stats, setStats] = useState<SmartOLTBillingSuspensionStats | null>(null)
  const [selectedTab, setSelectedTab] = useState<StatusTab>("all")
  const [searchInput, setSearchInput] = useState("")
  const [searchQuery, setSearchQuery] = useState("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [total, setTotal] = useState(0)

  // Single action modal states
  const [reactivateModalOpen, setReactivateModalOpen] = useState(false)
  const [selectedSuspension, setSelectedSuspension] = useState<SmartOLTBillingSuspensionRow | null>(null)
  const [reactivateReason, setReactivateReason] = useState("")
  const [reactivateDays, setReactivateDays] = useState(5)
  const [submittingAction, setSubmittingAction] = useState(false)

  const [resuspendModalOpen, setResuspendModalOpen] = useState(false)
  const [resuspendReason, setResuspendReason] = useState("")

  // Bulk selection & modal states
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkModalOpen, setBulkModalOpen] = useState(false)
  const [bulkItems, setBulkItems] = useState<Array<{ row: SmartOLTBillingSuspensionRow; durationDays: number }>>([])
  const [bulkReason, setBulkReason] = useState("Customer promised payment after temporary grace period")
  const [bulkGlobalDuration, setBulkGlobalDuration] = useState(30)

  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const pageSafe = Math.min(page, totalPages)

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    setRefreshing(true)
    try {
      const [listRes, statsRes] = await Promise.all([
        smartoltService.getBillingSuspensions({
          status: selectedTab === "all" ? undefined : selectedTab,
          search: searchQuery.trim() || undefined,
          page: pageSafe,
          per_page: pageSize,
        }),
        smartoltService.getBillingSuspensionStats(),
      ])

      setSuspensions(listRes.suspensions || [])
      setTotal(Number(listRes.total || 0))
      setStats(statsRes)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to load billing suspensions")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [pageSafe, pageSize, searchQuery, selectedTab])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Debounce search input typing to ensure smooth, responsive search
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim()
      if (trimmed !== searchQuery) {
        setPage(1)
        setSearchQuery(trimmed)
      }
    }, 350)
    return () => clearTimeout(timer)
  }, [searchInput, searchQuery])

  // Clear selections whenever tab or search filter changes
  useEffect(() => {
    setSelectedIds(new Set())
  }, [selectedTab, searchQuery])

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = searchInput.trim()
    setPage(1)
    setSearchQuery(trimmed)
  }

  const handleClearSearch = () => {
    setSearchInput("")
    setSearchQuery("")
    setPage(1)
  }

  // Row selection helpers
  const isRowSelectable = (row: SmartOLTBillingSuspensionRow) => row.status !== "resolved_paid"

  const selectableRows = useMemo(() => {
    return suspensions.filter(isRowSelectable)
  }, [suspensions])

  const isAllSelected = selectableRows.length > 0 && selectableRows.every((r) => selectedIds.has(r.id))
  const isSomeSelected = selectableRows.some((r) => selectedIds.has(r.id)) && !isAllSelected

  const masterCheckboxRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (masterCheckboxRef.current) {
      masterCheckboxRef.current.indeterminate = isSomeSelected
    }
  }, [isSomeSelected])

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        selectableRows.forEach((r) => next.delete(r.id))
        return next
      })
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        selectableRows.forEach((r) => next.add(r.id))
        return next
      })
    }
  }

  const handleToggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleClearSelection = () => {
    setSelectedIds(new Set())
  }

  const selectedTotalOverdue = useMemo(() => {
    return suspensions
      .filter((r) => selectedIds.has(r.id))
      .reduce((sum, r) => sum + (Number(r.amount_overdue) || 0), 0)
  }, [suspensions, selectedIds])

  const handleRunWorkerNow = async () => {
    setWorkerRunning(true)
    try {
      const res = await smartoltService.runBillingWorkerNow()
      // If stats is empty the backend signals billing enforcement is disabled.
      const isDisabled = !res.stats || Object.keys(res.stats).length === 0
      if (isDisabled) {
        toast.info(res.message || "Billing enforcement is disabled. Enable \"Auto disable ONU on overdue invoices\" in System Config → Billing Policy.")
      } else {
        toast.success(
          `Worker finished. Checked ${res.stats?.customers_evaluated || 0}, suspended ${res.stats?.newly_suspended || 0}.`,
        )
        await loadData(true)
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to execute worker cycle")
    } finally {
      setWorkerRunning(false)
    }
  }

  const openReactivateModal = (item: SmartOLTBillingSuspensionRow) => {
    setSelectedSuspension(item)
    setReactivateReason("Customer promised payment after temporary grace period")
    setReactivateDays(5)
    setReactivateModalOpen(true)
  }

  const handleConfirmReactivate = async () => {
    if (!selectedSuspension) return
    if (reactivateDays < 1 || reactivateDays > 30) {
      toast.error("Temporary reactivation must be between 1 and 30 days")
      return
    }
    setSubmittingAction(true)
    try {
      await smartoltService.reactivateBillingSuspension(selectedSuspension.id, reactivateReason, reactivateDays)
      toast.success(`Customer reactivation queued for ${reactivateDays} day${reactivateDays === 1 ? "" : "s"}`)
      setReactivateModalOpen(false)
      setSelectedSuspension(null)
      await loadData(true)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to reactivate customer")
    } finally {
      setSubmittingAction(false)
    }
  }

  const openResuspendModal = (item: SmartOLTBillingSuspensionRow) => {
    setSelectedSuspension(item)
    setResuspendReason("Payment extension period expired without settlement")
    setResuspendModalOpen(true)
  }

  const handleConfirmResuspend = async () => {
    if (!selectedSuspension) return
    setSubmittingAction(true)
    try {
      await smartoltService.resuspendBillingSuspension(selectedSuspension.id, resuspendReason)
      toast.success("Customer re-suspension command queued")
      setResuspendModalOpen(false)
      setSelectedSuspension(null)
      await loadData(true)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to re-suspend customer")
    } finally {
      setSubmittingAction(false)
    }
  }

  // Bulk Reactivate Modal Actions
  const openBulkReactivateModal = () => {
    const chosen = suspensions.filter((r) => selectedIds.has(r.id) && isRowSelectable(r))
    if (chosen.length === 0) {
      toast.error("Please select at least one customer to reactivate")
      return
    }

    // Default duration is set to 30 days automatically for all users
    setBulkItems(
      chosen.map((row) => ({
        row,
        durationDays: 30,
      }))
    )
    setBulkGlobalDuration(30)
    setBulkReason("Customer promised payment after temporary grace period")
    setBulkModalOpen(true)
  }

  const handleBulkItemDurationChange = (suspensionId: string, days: number) => {
    const safeDays = Math.max(1, Math.min(30, days || 1))
    setBulkItems((prev) =>
      prev.map((item) => (item.row.id === suspensionId ? { ...item, durationDays: safeDays } : item))
    )
  }

  const handleApplyGlobalDuration = (days: number) => {
    const safeDays = Math.max(1, Math.min(30, days || 1))
    setBulkGlobalDuration(safeDays)
    setBulkItems((prev) => prev.map((item) => ({ ...item, durationDays: safeDays })))
  }

  const handleRemoveFromBulk = (suspensionId: string) => {
    setBulkItems((prev) => prev.filter((item) => item.row.id !== suspensionId))
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.delete(suspensionId)
      return next
    })
  }

  const handleConfirmBulkReactivate = async () => {
    if (bulkItems.length === 0) {
      toast.error("No customers in bulk list")
      return
    }
    setSubmittingAction(true)
    try {
      const payloadItems = bulkItems.map((item) => ({
        suspension_id: item.row.id,
        duration_days: item.durationDays,
      }))
      const res = await smartoltService.bulkReactivateBillingSuspensions(payloadItems, bulkReason)
      toast.success(
        res.message || `Successfully queued reactivation for ${res.reactivated_count || payloadItems.length} customer(s)`
      )
      if (res.failed_count && res.failed_count > 0) {
        toast.error(`${res.failed_count} customer(s) could not be reactivated`)
      }
      setBulkModalOpen(false)
      setBulkItems([])
      setSelectedIds(new Set())
      await loadData(true)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to execute bulk reactivation")
    } finally {
      setSubmittingAction(false)
    }
  }

  const tabCounts = useMemo(
    () => ({
      suspended_unpaid: stats?.currently_suspended || 0,
      temporary_reactivated: stats?.temporary_reactivated || 0,
      external_override_detected: stats?.external_overrides || 0,
      manually_exempted: stats?.manually_exempted || 0,
      resolved_paid: stats?.resolved_today || 0,
    }),
    [stats],
  )

  const fromRow = total === 0 ? 0 : (pageSafe - 1) * pageSize + 1
  const toRow = Math.min(pageSafe * pageSize, total)

  return (
    <div className="space-y-4 sm:space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Billing Suspensions</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            SmartOLT billing enforcement, temporary reactivations, external overrides, and paid restorations.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {selectedIds.size > 0 && (
            <button
              type="button"
              onClick={openBulkReactivateModal}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 text-sm font-semibold text-white shadow-xs transition-all hover:bg-emerald-700 animate-fadeIn"
            >
              <ShieldCheck className="h-4 w-4" />
              Reactivate Selected ({selectedIds.size})
            </button>
          )}
          <span className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 text-xs font-semibold text-blue-700">
            <SlidersHorizontal className="h-3.5 w-3.5" />
            24h grace policy
          </span>
          <button
            type="button"
            disabled={workerRunning}
            onClick={handleRunWorkerNow}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white shadow-xs transition-all hover:bg-blue-700 disabled:opacity-50"
          >
            <Play className={`h-3.5 w-3.5 ${workerRunning ? "animate-spin" : ""}`} />
            {workerRunning ? "Running..." : "Run Worker"}
          </button>
          <button
            type="button"
            disabled={refreshing}
            onClick={() => loadData(true)}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-xs transition-all hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 text-slate-500 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Currently Suspended"
          value={stats?.currently_suspended ?? 0}
          hint="Unpaid and past grace"
          tone="rose"
          icon={Ban}
        />
        <StatTile
          label="Temporary Reactivated"
          value={stats?.temporary_reactivated ?? 0}
          hint="Timed grace windows"
          tone="amber"
          icon={Clock}
        />
        <StatTile
          label="External Overrides"
          value={stats?.external_overrides ?? 0}
          hint="Enabled outside app"
          tone="amber"
          icon={AlertTriangle}
        />
        <StatTile
          label="Overdue Balance"
          value={formatCompactPkr(stats?.total_overdue_amount ?? 0)}
          hint="Open billing actions"
          tone="slate"
          icon={DollarSign}
        />
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs">
        <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/80 p-3.5 sm:flex-row sm:items-center sm:justify-between sm:p-4">
          <div className="flex gap-1 overflow-x-auto rounded-lg border border-slate-200 bg-white p-1 shadow-xs sm:flex-wrap sm:overflow-visible">
            {tabs.map((tab) => {
              const count =
                tab.key === "all"
                  ? total
                  : tab.key === "suspended_unpaid"
                    ? tabCounts.suspended_unpaid
                    : tab.key === "temporary_reactivated"
                      ? tabCounts.temporary_reactivated
                      : tab.key === "external_override_detected"
                        ? tabCounts.external_override_detected
                        : tab.key === "manually_exempted"
                          ? tabCounts.manually_exempted
                          : tabCounts.resolved_paid
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setSelectedTab(tab.key)}
                  className={`inline-flex h-8 shrink-0 items-center gap-2 rounded-md px-3 text-xs font-semibold transition-all ${
                    selectedTab === tab.key
                      ? "bg-blue-600 text-white shadow-xs"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <span>{tab.label}</span>
                  {count > 0 && (
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[10px] ${
                        selectedTab === tab.key ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          <form onSubmit={handleSearchSubmit} className="flex w-full gap-2 sm:w-auto">
            <div className="relative min-w-0 flex-1 sm:w-80">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search customer, invoice, ONU..."
                className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-900 placeholder-slate-400 shadow-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <button
              type="submit"
              className="inline-flex h-9 items-center rounded-lg bg-slate-900 px-3 text-sm font-semibold text-white shadow-xs transition-all hover:bg-slate-800"
            >
              Search
            </button>
            {(searchInput || searchQuery) && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-xs transition-all hover:bg-slate-50"
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </form>
        </div>

        {/* Floating / Sticky Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-blue-100 bg-blue-50/90 px-4 py-2.5 transition-all">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white shadow-xs">
                {selectedIds.size}
              </span>
              <span className="text-sm font-semibold text-blue-950">
                {selectedIds.size} customer{selectedIds.size === 1 ? "" : "s"} selected
              </span>
              <span className="text-xs text-blue-700">
                (Rs. {selectedTotalOverdue.toLocaleString()} overdue)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleClearSelection}
                className="inline-flex h-8 items-center rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 shadow-xs hover:bg-slate-50"
              >
                Deselect all
              </button>
              <button
                type="button"
                onClick={openBulkReactivateModal}
                className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 text-xs font-semibold text-white shadow-xs transition-all hover:bg-emerald-700"
              >
                <ShieldCheck className="h-4 w-4" />
                Reactivate Selected ({selectedIds.size})
              </button>
            </div>
          </div>
        )}

        <div className="md:hidden">
          <div className="divide-y divide-slate-100 bg-white">
            {loading ? (
              <div className="flex flex-col items-center gap-2 px-4 py-12 text-slate-500">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
                <span className="text-xs">Loading billing suspensions...</span>
              </div>
            ) : suspensions.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500" />
                <h3 className="mt-3 text-sm font-semibold text-slate-900">No records found</h3>
                <p className="mt-1 text-xs text-slate-500">There are no billing suspension records for the selected filter.</p>
              </div>
            ) : (
              suspensions.map((row) => {
                const meta = statusMeta(row.status)
                const StatusIcon = meta.icon
                const isSuspended = row.status === "suspended_unpaid"
                const isTemporary = row.status === "temporary_reactivated"
                const isExempted = row.status === "manually_exempted"
                const isExternal = row.status === "external_override_detected"
                const isPaid = row.status === "resolved_paid"
                const isSelectable = isRowSelectable(row)
                const isSelected = selectedIds.has(row.id)
                return (
                  <div key={row.id} className={`p-3 ${isSelected ? "bg-blue-50/70" : "bg-white"}`}>
                    <div className="flex items-start gap-3">
                      {isSelectable ? (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleRow(row.id)}
                          aria-label={`Select ${row.customer_name}`}
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                      ) : (
                        <span className="mt-1 inline-block h-4 w-4" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-bold text-slate-900">{row.customer_name || "-"}</div>
                            <div className="mt-0.5 font-mono text-xs text-blue-600">{row.internet_id || "-"}</div>
                          </div>
                          <span className={`inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold ${meta.className}`}>
                            <StatusIcon className="h-3 w-3" />
                            {meta.label}
                          </span>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                          <div className="rounded-lg bg-slate-50 px-2.5 py-2">
                            <div className="text-[10px] font-semibold uppercase text-slate-500">Overdue</div>
                            <div className="mt-0.5 font-bold text-slate-900">Rs. {Number(row.amount_overdue || 0).toLocaleString()}</div>
                          </div>
                          <div className="rounded-lg bg-slate-50 px-2.5 py-2">
                            <div className="text-[10px] font-semibold uppercase text-slate-500">Invoice</div>
                            <div className="mt-0.5 truncate font-mono font-semibold text-slate-800">{row.invoice_number || "-"}</div>
                          </div>
                          <div className="rounded-lg bg-slate-50 px-2.5 py-2">
                            <div className="text-[10px] font-semibold uppercase text-slate-500">ONU</div>
                            <Link to={SMARTOLT_PATHS.onuDetail(row.onu_external_id)} className="mt-0.5 block truncate font-mono font-semibold text-blue-600">
                              {row.onu_external_id}
                            </Link>
                          </div>
                          <div className="rounded-lg bg-slate-50 px-2.5 py-2">
                            <div className="text-[10px] font-semibold uppercase text-slate-500">SmartOLT</div>
                            <div className="mt-0.5 truncate font-semibold text-slate-800">{row.last_smartolt_admin_status || row.onu_status || "-"}</div>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-slate-500">
                          Due {formatDate(row.due_date)} · Suspended {formatDate(row.suspended_at, true)}
                        </div>
                        {(isTemporary || isExternal || isExempted) && (
                          <div className="mt-2 rounded-lg border border-amber-100 bg-amber-50 px-2.5 py-2 text-xs text-amber-900">
                            {row.temporary_reactivation_reason || row.exemption_reason || row.external_override_note || "Owner action recorded."}
                          </div>
                        )}
                        <div className="mt-3 flex flex-wrap gap-2">
                          {(isSuspended || isExternal) && (
                            <button type="button" onClick={() => openReactivateModal(row)} className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white shadow-xs">
                              <ShieldCheck className="h-3.5 w-3.5" />
                              Reactivate
                            </button>
                          )}
                          {(isTemporary || isExempted) && (
                            <button type="button" onClick={() => openResuspendModal(row)} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 text-xs font-semibold text-rose-700 shadow-xs">
                              <Ban className="h-3.5 w-3.5" />
                              Re-suspend
                            </button>
                          )}
                          {isPaid && (
                            <span className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 text-xs font-semibold text-emerald-700">
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              Restored
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        <div className="hidden overflow-x-auto md:block">
          <table className="min-w-[1320px] w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
              <tr>
                <th className="w-10 px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    ref={masterCheckboxRef}
                    checked={isAllSelected}
                    onChange={handleToggleSelectAll}
                    disabled={loading || selectableRows.length === 0}
                    aria-label="Select all"
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                  />
                </th>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-left">ONU</th>
                <th className="px-4 py-3 text-left">Invoice</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">SmartOLT State</th>
                <th className="px-4 py-3 text-left">Timeline</th>
                <th className="px-4 py-3 text-left">Owner Note</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white text-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2 text-slate-500">
                      <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
                      <span className="text-xs">Loading billing suspensions...</span>
                    </div>
                  </td>
                </tr>
              ) : suspensions.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <div className="mx-auto max-w-sm">
                      <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500" />
                      <h3 className="mt-3 text-sm font-semibold text-slate-900">No records found</h3>
                      <p className="mt-1 text-xs text-slate-500">
                        There are no billing suspension records for the selected filter.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                suspensions.map((row) => {
                  const meta = statusMeta(row.status)
                  const StatusIcon = meta.icon
                  const isSuspended = row.status === "suspended_unpaid"
                  const isTemporary = row.status === "temporary_reactivated"
                  const isExempted = row.status === "manually_exempted"
                  const isExternal = row.status === "external_override_detected"
                  const isPaid = row.status === "resolved_paid"
                  const isSelectable = isRowSelectable(row)
                  const isSelected = selectedIds.has(row.id)

                  return (
                    <tr
                      key={row.id}
                      className={`transition-colors ${
                        isSelected ? "bg-blue-50/70 hover:bg-blue-50/90" : "hover:bg-slate-50/80"
                      }`}
                    >
                      <td className="px-4 py-3 align-top">
                        {isSelectable ? (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleRow(row.id)}
                            aria-label={`Select ${row.customer_name}`}
                            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          />
                        ) : (
                          <span className="inline-block w-4" />
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="font-semibold text-slate-900">{row.customer_name || "-"}</div>
                        <div className="mt-0.5 font-mono text-xs text-blue-600">{row.internet_id || "-"}</div>
                        <div className="mt-1 text-xs text-slate-500">{row.phone || "No phone"}</div>
                        {row.address && (
                          <div className="mt-1 max-w-[260px] truncate text-xs text-slate-400" title={row.address}>
                            {row.address}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <Link
                          to={SMARTOLT_PATHS.onuDetail(row.onu_external_id)}
                          className="font-mono text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {row.onu_external_id}
                        </Link>
                        <div className="mt-1 text-xs text-slate-500">SN: {row.onu_sn || "-"}</div>
                        <div className="mt-1 text-xs text-slate-500">
                          Port: {row.board || "-"}/{row.port || "-"} #{row.onu_number || "-"}
                        </div>
                        {row.onu_status && (
                          <div className="mt-1 text-[11px] text-slate-400">ONU status: {row.onu_status}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="font-semibold text-slate-900">
                          Rs. {Number(row.amount_overdue || 0).toLocaleString()}
                        </div>
                        <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                          <FileText className="h-3.5 w-3.5 text-slate-400" />
                          <span>{row.invoice_number || "-"}</span>
                        </div>
                        <div className="mt-1 text-xs text-slate-500">Due: {formatDate(row.due_date)}</div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-semibold ${meta.className}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                          <StatusIcon className="h-3.5 w-3.5" />
                          {meta.label}
                        </span>
                        {isTemporary && row.temporary_reactivation_until && (
                          <div className="mt-2 text-[11px] font-medium text-amber-700">
                            Until {formatDate(row.temporary_reactivation_until, true)}
                          </div>
                        )}
                        {isExternal && (
                          <div className="mt-2 max-w-[220px] text-[11px] text-orange-700">
                            SmartOLT was enabled outside app.
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-xs text-slate-500">
                        <div>
                          Admin:{" "}
                          <span className={`font-semibold ${
                            String(row.last_smartolt_admin_status || "").toLowerCase() === "enabled"
                              ? "text-emerald-700"
                              : String(row.last_smartolt_admin_status || "").toLowerCase() === "disabled"
                                ? "text-rose-700"
                                : "text-slate-600"
                          }`}>
                            {row.last_smartolt_admin_status || "-"}
                          </span>
                        </div>
                        <div className="mt-1">ONU: {row.onu_status || "-"}</div>
                        <div className="mt-1 text-[11px] text-slate-400">
                          Checked: {formatDate(row.last_smartolt_status_checked_at, true)}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top text-xs text-slate-500">
                        <div>Suspended: {formatDate(row.suspended_at, true)}</div>
                        <div className="mt-1">Grace expired: {formatDate(row.grace_period_expired_at, true)}</div>
                        {isPaid && <div className="mt-1 text-emerald-700">Resolved: {formatDate(row.resolved_at, true)}</div>}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {isTemporary || isExempted ? (
                          <div className="max-w-xs">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-800">
                              <UserCheck className="h-3.5 w-3.5 text-amber-600" />
                              {row.exempted_by_name || "Company owner"}
                            </div>
                            {(row.temporary_reactivation_reason || row.exemption_reason) && (
                              <div className="mt-1 rounded-lg border border-amber-100 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                                {row.temporary_reactivation_reason || row.exemption_reason}
                              </div>
                            )}
                            {isTemporary && row.temporary_reactivation_days && (
                              <div className="mt-1 text-[11px] font-semibold text-amber-700">
                                {row.temporary_reactivation_days} day grace
                              </div>
                            )}
                            <div className="mt-1 text-[11px] text-slate-400">{formatDate(row.reactivated_at || row.exempted_at, true)}</div>
                          </div>
                        ) : isExternal ? (
                          <div className="max-w-xs rounded-lg border border-orange-100 bg-orange-50 px-2 py-1.5 text-xs text-orange-800">
                            {row.external_override_note || "External SmartOLT enable detected."}
                          </div>
                        ) : isPaid ? (
                          <span className="text-xs text-slate-500">Paid and marked resolved.</span>
                        ) : (
                          <span className="text-xs text-slate-400">No owner exemption</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right align-top">
                        {(isSuspended || isExternal) && (
                          <button
                            type="button"
                            onClick={() => openReactivateModal(row)}
                            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white shadow-xs transition-all hover:bg-emerald-700"
                          >
                            <ShieldCheck className="h-3.5 w-3.5" />
                            Reactivate
                          </button>
                        )}
                        {(isTemporary || isExempted) && (
                          <button
                            type="button"
                            onClick={() => openResuspendModal(row)}
                            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 text-xs font-semibold text-rose-700 shadow-xs transition-all hover:bg-rose-50"
                          >
                            <Ban className="h-3.5 w-3.5" />
                            Re-suspend
                          </button>
                        )}
                        {isPaid && (
                          <span className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 text-xs font-semibold text-emerald-700">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Restored
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/80 px-4 py-3 text-xs text-slate-600">
          <div className="flex items-center gap-2">
            <select
              aria-label="Rows per page"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-slate-700 shadow-xs"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span>
              Showing {fromRow} to {toRow} of {total} records
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={pageSafe <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 shadow-xs hover:bg-slate-50 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1)
              .filter((n) => n === 1 || n === totalPages || Math.abs(n - pageSafe) <= 2)
              .reduce<number[]>((acc, n) => {
                if (acc.length && n - acc[acc.length - 1] > 1) acc.push(-n)
                acc.push(n)
                return acc
              }, [])
              .map((n) =>
                n < 0 ? (
                  <span key={`e${n}`} className="px-1 text-slate-400">
                    ...
                  </span>
                ) : (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPage(n)}
                    className={`inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-2 font-medium shadow-xs ${
                      n === pageSafe
                        ? "border-blue-600 bg-blue-600 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {n}
                  </button>
                ),
              )}
            <button
              type="button"
              disabled={pageSafe >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 shadow-xs hover:bg-slate-50 disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Reactivate Modal */}
      {bulkModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-xs">
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Bulk Temporary Reactivation</h3>
                  <p className="text-xs text-slate-500">
                    Reactivate {bulkItems.length} selected customer{bulkItems.length === 1 ? "" : "s"} with automated grace tracking.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setBulkModalOpen(false)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
              {/* Batch Duration Toolbar */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                      Set Duration for All Customers
                    </span>
                    <p className="text-[11px] text-slate-500">
                      Default is 30 days. You can also customize each customer individually in the list below.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {[7, 15, 30].map((days) => (
                      <button
                        key={days}
                        type="button"
                        onClick={() => handleApplyGlobalDuration(days)}
                        className={`rounded-lg border px-3 py-1 text-xs font-semibold transition-all ${
                          bulkGlobalDuration === days
                            ? "border-blue-600 bg-blue-600 text-white shadow-xs"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        {days} Days
                      </button>
                    ))}
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min={1}
                        max={30}
                        value={bulkGlobalDuration}
                        onChange={(e) => setBulkGlobalDuration(Number(e.target.value))}
                        className="h-7 w-16 rounded-md border border-slate-200 bg-white px-2 text-center text-xs text-slate-900 shadow-xs focus:border-blue-500 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => handleApplyGlobalDuration(bulkGlobalDuration)}
                        className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                      >
                        Apply All
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer List */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                    Selected Customers ({bulkItems.length})
                  </label>
                  <span className="text-xs text-slate-500">
                    Total Overdue: Rs. {bulkItems.reduce((acc, item) => acc + (Number(item.row.amount_overdue) || 0), 0).toLocaleString()}
                  </span>
                </div>
                <div className="max-h-64 divide-y divide-slate-100 overflow-y-auto rounded-xl border border-slate-200 bg-white">
                  {bulkItems.length === 0 ? (
                    <div className="p-6 text-center text-xs text-slate-500">No customers selected</div>
                  ) : (
                    bulkItems.map(({ row, durationDays }) => (
                      <div
                        key={row.id}
                        className="flex flex-wrap items-center justify-between gap-3 p-3 transition-colors hover:bg-slate-50"
                      >
                        <div className="min-w-[200px] flex-1">
                          <div className="font-semibold text-slate-900 text-sm">{row.customer_name || "-"}</div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <span className="font-mono text-blue-600">{row.internet_id || "-"}</span>
                            <span>•</span>
                            <span className="font-mono">{row.onu_external_id}</span>
                            <span>•</span>
                            <span className="font-semibold text-slate-700">
                              Rs. {Number(row.amount_overdue || 0).toLocaleString()}
                            </span>
                          </div>
                        </div>

                        {/* Individual Duration Control */}
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-medium text-slate-500">Duration:</span>
                            <input
                              type="number"
                              min={1}
                              max={30}
                              value={durationDays}
                              onChange={(e) => handleBulkItemDurationChange(row.id, Number(e.target.value))}
                              className="h-8 w-16 rounded-lg border border-slate-200 bg-white px-2 text-center text-xs font-semibold text-slate-900 shadow-xs focus:border-blue-500 focus:outline-none"
                            />
                            <span className="text-xs text-slate-500">days</span>
                          </div>

                          <div className="flex gap-1">
                            {[15, 30].map((d) => (
                              <button
                                key={d}
                                type="button"
                                onClick={() => handleBulkItemDurationChange(row.id, d)}
                                className={`rounded px-1.5 py-0.5 text-[11px] font-medium border ${
                                  durationDays === d
                                    ? "border-blue-600 bg-blue-50 text-blue-700"
                                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                                }`}
                              >
                                {d}d
                              </button>
                            ))}
                          </div>

                          <button
                            type="button"
                            onClick={() => handleRemoveFromBulk(row.id)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                            title="Remove from bulk list"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Customer Promise / Reason */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Customer Promise / Reactivation Note
                </label>
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {[
                    "Customer promised payment after salary",
                    "Customer requested short grace period",
                    "Billing dispute under review",
                    "Bulk payment extension",
                  ].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setBulkReason(preset)}
                      className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                    >
                      {preset}
                    </button>
                  ))}
                </div>
                <textarea
                  rows={2}
                  value={bulkReason}
                  onChange={(e) => setBulkReason(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 p-3 text-sm text-slate-900 shadow-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter reason for temporary reactivation..."
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between border-t border-slate-100 px-6 py-4">
              <div className="text-xs text-slate-500">
                Worker will monitor dues and re-suspend any user whose payment extension expires.
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={submittingAction}
                  onClick={() => setBulkModalOpen(false)}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={submittingAction || bulkItems.length === 0}
                  onClick={handleConfirmBulkReactivate}
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-emerald-600 px-4 text-sm font-semibold text-white shadow-xs transition-all hover:bg-emerald-700 disabled:opacity-50"
                >
                  <ShieldCheck className="h-4 w-4" />
                  {submittingAction ? "Reactivating..." : `Confirm & Reactivate (${bulkItems.length})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Single Reactivate Modal */}
      {reactivateModalOpen && selectedSuspension && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-xs">
          <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <h3 className="text-base font-bold text-slate-900">Temporary Reactivation</h3>
                <p className="mt-0.5 text-xs text-slate-500">Opens service for a limited number of days, then enforcement resumes.</p>
              </div>
              <button
                type="button"
                onClick={() => setReactivateModalOpen(false)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                <div className="font-semibold text-slate-900">{selectedSuspension.customer_name}</div>
                <div className="mt-1">Internet ID: {selectedSuspension.internet_id}</div>
                <div>ONU: {selectedSuspension.onu_external_id}</div>
                <div>Overdue: Rs. {Number(selectedSuspension.amount_overdue || 0).toLocaleString()}</div>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Reactivation duration
                </label>
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {[1, 3, 5, 7, 15, 30].map((days) => (
                    <button
                      key={days}
                      type="button"
                      onClick={() => setReactivateDays(days)}
                      className={`rounded-md border px-2.5 py-1 text-xs font-semibold ${
                        reactivateDays === days
                          ? "border-blue-600 bg-blue-50 text-blue-700"
                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {days}d
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  min={1}
                  max={30}
                  value={reactivateDays}
                  onChange={(e) => setReactivateDays(Number(e.target.value))}
                  className="h-10 w-32 rounded-xl border border-slate-200 px-3 text-sm text-slate-900 shadow-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-slate-500">
                  If dues are still unpaid after this period, the worker will suspend the ONU again.
                </p>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Customer promise / reason
                </label>
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {[
                    "Customer promised payment after salary",
                    "Customer requested short grace period",
                    "Billing dispute under review",
                    "Bank transfer verification pending",
                  ].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setReactivateReason(preset)}
                      className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                    >
                      {preset}
                    </button>
                  ))}
                </div>
                <textarea
                  rows={3}
                  value={reactivateReason}
                  onChange={(e) => setReactivateReason(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 p-3 text-sm text-slate-900 shadow-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-4">
              <button
                type="button"
                disabled={submittingAction}
                onClick={() => setReactivateModalOpen(false)}
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submittingAction}
                onClick={handleConfirmReactivate}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                <ShieldCheck className="h-4 w-4" />
                {submittingAction ? "Queuing..." : `Reactivate ${reactivateDays || ""}d`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single Resuspend Modal */}
      {resuspendModalOpen && selectedSuspension && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-xs">
          <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <h3 className="text-base font-bold text-slate-900">Re-suspend Customer</h3>
                <p className="mt-0.5 text-xs text-slate-500">Queues SmartOLT disable command for this ONU.</p>
              </div>
              <button
                type="button"
                onClick={() => setResuspendModalOpen(false)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              <p className="text-sm text-slate-600">
                End the temporary reactivation for <strong>{selectedSuspension.customer_name}</strong> and suspend the ONU again.
              </p>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Re-suspension reason
                </label>
                <input
                  type="text"
                  value={resuspendReason}
                  onChange={(e) => setResuspendReason(e.target.value)}
                  className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm text-slate-900 shadow-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-4">
              <button
                type="button"
                disabled={submittingAction}
                onClick={() => setResuspendModalOpen(false)}
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submittingAction}
                onClick={handleConfirmResuspend}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-rose-600 px-3 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
              >
                <Ban className="h-4 w-4" />
                {submittingAction ? "Queuing..." : "Re-suspend"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
